import { COACH_ROUTE_ACCOUNT, COACH_ROUTE_BADGES, COACH_ROUTE_INDEX, COACH_ROUTE_JOB_CANDIDATES, COACH_ROUTE_LEARN, COACH_ROUTE_LESSONS, COACH_ROUTE_PORTFOLIO, COACH_ROUTE_PROFILE, COACH_ROUTE_REWARDS, COACH_ROUTE_SAVED } from '~/constants/routes/coach';

/**
 * The pages the coach catalogue names. These are wire values, not URLs: an action recommendation
 * arrives carrying one (`CoachNavigationPath`), and every in-app caller asks for a page by the same
 * name. They are free of the URL and stay put when it moves.
 */
export type CoachPage = 'wallet' | 'rewards' | 'saved' | 'account' | 'learn-library' | 'my-lessons' | 'job-candidates' | 'portfolio';

/** A catalogue page and the `/coach` child route that serves it. Only three of the eight differ. */
const ROUTE_FOR_PAGE: Record<CoachPage, string> = {
  'wallet': COACH_ROUTE_BADGES,
  'learn-library': COACH_ROUTE_LEARN,
  'my-lessons': COACH_ROUTE_LESSONS,
  'rewards': COACH_ROUTE_REWARDS,
  'saved': COACH_ROUTE_SAVED,
  'account': COACH_ROUTE_ACCOUNT,
  'job-candidates': COACH_ROUTE_JOB_CANDIDATES,
  'portfolio': COACH_ROUTE_PORTFOLIO,
};

/**
 * Whether a name is one of the catalogue's own, which is the only thing `coachRoutePath` actually
 * translates. Asked by callers holding a name from outside the app — an old deep link's
 * `?navigation=` — where a path this file does not recognise has to be turned away rather than
 * spelled out as a `/coach/<whatever>` no page serves.
 *
 * Ownership, not truthiness: the map is an object literal, so `constructor` and `toString` are
 * names it answers to without being names it holds, and a bracket lookup would hand their inherited
 * value on to be spelled into a URL.
 */
export const isCoachPage = (value: string): value is CoachPage => Object.hasOwn(ROUTE_FOR_PAGE, value);

/**
 * The one translation from a catalogue path to the route that serves it.
 *
 * A catalogue path is a page name and then that page's own segments — `wallet`,
 * `rewards/westpac-cashout/faq` — so only the first segment is a name this file knows; the rest
 * belong to the page and are carried through untouched. A path already written in route segments
 * passes through unchanged, because no catalogue page is called `badges` or `learn`, which is what
 * lets the shell hand this the path it read back off the URL.
 */
export const coachRoutePath = (cataloguePath: string): string => {
  const [page = '', ...rest] = cataloguePath.replace(/^\/+/, '').split('/');
  const base = isCoachPage(page)
    ? ROUTE_FOR_PAGE[page]
    : (page ? `${COACH_ROUTE_INDEX}/${page}` : COACH_ROUTE_INDEX);

  return [base, ...rest.filter(Boolean)].join('/');
};

/**
 * Whether a history entry is the profile page — asked by the surfaces both the profile and the
 * chat can open (the Saved page's tile vs a coach recommendation card), whose back arrow returns
 * through whichever door the student actually used. The value comes off the router history's
 * `state.back`, so it is unknown-typed and may carry a query string; anything else — another
 * route, a missing entry on a deep link or fresh load — is not the profile.
 */
export const isProfileRoute = (value: unknown): boolean =>
  typeof value === 'string' && (value === COACH_ROUTE_PROFILE || value.startsWith(`${COACH_ROUTE_PROFILE}?`));
